import { useState, useEffect } from "react";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Gauge, Flag, CheckCircle2, ChevronRight } from "lucide-react";

type Rag = "green" | "amber" | "red";

interface RagOption {
  value: Rag;
  emoji: string;
  label: string;
  selected: string;
}

const RAG_OPTIONS: RagOption[] = [
  {
    value: "green",
    emoji: "🟢",
    label: "Real progress this week",
    selected: "border-green-500 bg-green-500/10 ring-2 ring-green-500/40",
  },
  {
    value: "amber",
    emoji: "🟠",
    label: "Some movement",
    selected: "border-yellow-500 bg-yellow-500/10 ring-2 ring-yellow-500/40",
  },
  {
    value: "red",
    emoji: "🔴",
    label: "I need a hand",
    selected: "border-red-500 bg-red-500/10 ring-2 ring-red-500/40",
  },
];

const AIAgentVelocityCheckin = () => {
  const [rag, setRag] = useState<Rag | null>(null);
  const [movedForward, setMovedForward] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [stuck, setStuck] = useState("");
  const [needsHelp, setNeedsHelp] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidLink, setInvalidLink] = useState(false);

  useEffect(() => {
    // The check-in must be opened from the personalised link in the weekly email.
    if (!new URLSearchParams(window.location.search).get("e")) setInvalidLink(true);
  }, []);

  const canSubmit = rag !== null && !submitting;

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    const params = new URLSearchParams(window.location.search);
    const token = params.get("e");
    const nRaw = params.get("n");
    const checkIn = nRaw ? Number(nRaw) : undefined;
    try {
      const res = await fetch("/api/forms/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          form: "velocity",
          token,
          checkIn,
          fields: { rag, movedForward, nextStep, stuck, needsHelp },
        }),
      });
      if (!res.ok) {
        setError("Sorry, we couldn't save that. Please try again.");
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Sorry, we couldn't save that. Please try again.");
      setSubmitting(false);
    }
  };

  if (invalidLink) {
    return (
      <div className="min-h-screen bg-background">
        <SEO title="AI Agents Velocity Check-In" description="Weekly velocity check-in." />
        <Navigation />
        <section className="pt-32 pb-20">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">Open this from your email</h1>
            <p className="text-lg text-muted-foreground">
              This check-in opens from the personalised link in your weekly email — please tap
              the button in that email so we know it's you.
            </p>
          </div>
        </section>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <SEO title="Velocity check-in — done" description="Your weekly velocity check-in." />
        <Navigation />
        <section className="pt-32 pb-20">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-6">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">That's this week logged</h1>
            <p className="text-lg text-muted-foreground">
              Thanks — this is exactly what keeps your momentum going.
              {needsHelp
                ? " We've noted you'd like a hand this week, and a coach will be in touch before Tuesday."
                : " If anything comes up before next week, just reply to any of our emails."}
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="AI Agents Velocity Check-In"
        description="Your 60-second weekly velocity check-in for the AI Agents Program. Tell us what you moved forward and where you'd like a hand."
        keywords={["AI Agents Program", "velocity check-in", "weekly check-in"]}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-primary/5 pt-32 pb-12">
        <div className="relative mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2.5 bg-primary/10 text-primary px-6 py-3 rounded-full text-lg font-medium mb-6">
            <Gauge className="h-5 w-5" />
            AI Agents · velocity check-in
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            <span className="bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
              Velocity check-in
            </span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Keeps your momentum going so we can help you achieve your goals.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <Card className="p-6 sm:p-8 space-y-10">
            {/* Status — where are you this week (RAG) */}
            <div>
              <h2 className="text-lg font-semibold mb-1">Where are you this week?</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Be honest — amber and red just tell us where to jump in.
              </p>
              <div className="grid sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Where are you this week?">
                {RAG_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={rag === opt.value}
                    onClick={() => setRag(opt.value)}
                    className={`text-left rounded-lg border p-4 transition-all ${
                      rag === opt.value ? opt.selected : "border-border hover:border-primary/40"
                    }`}
                  >
                    <div className="text-2xl mb-2">{opt.emoji}</div>
                    <div className="font-semibold">{opt.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Q1 — Moved forward (with aha / results as prompts) */}
            <div>
              <Label htmlFor="moved" className="text-lg font-semibold">
                What did you move forward on this week?
              </Label>
              <p className="text-sm text-muted-foreground mb-3 mt-1">
                What aha moments did you have? What results did you create using AI?
              </p>
              <Textarea
                id="moved"
                value={movedForward}
                onChange={(e) => setMovedForward(e.target.value)}
                placeholder="e.g. Got my first agent drafting my weekly update, and realised I can chain skills together…"
                rows={4}
              />
            </div>

            {/* Q2 — Aiming at next */}
            <div>
              <Label htmlFor="next" className="text-lg font-semibold">
                What are you aiming at next?
              </Label>
              <p className="text-sm text-muted-foreground mb-3 mt-1">
                What's the next outcome you're aiming for
              </p>
              <Textarea
                id="next"
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                placeholder="e.g. Connect it to my calendar so it pulls the week's meetings…"
                rows={3}
              />
            </div>

            {/* Q3 — Need a hand (optional) */}
            <div>
              <Label htmlFor="stuck" className="text-lg font-semibold">
                Need a hand with anything?
              </Label>
              <p className="text-sm text-muted-foreground mb-3 mt-1">
                Leave it blank if you're flying — but ask us anything, even the smallest thing.
              </p>
              <Textarea
                id="stuck"
                value={stuck}
                onChange={(e) => setStuck(e.target.value)}
                placeholder="e.g. Can't get the connection to authenticate…"
                rows={3}
              />
            </div>

            {/* Flag for help */}
            <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
              <div className="flex items-start gap-3">
                <Flag className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <Label htmlFor="needs-help" className="font-semibold">
                    Flag me for help this week
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Turn this on and a coach will reach out before Wednesday.
                  </p>
                </div>
              </div>
              <Switch id="needs-help" checked={needsHelp} onCheckedChange={setNeedsHelp} />
            </div>

            {/* Submit */}
            {error && (
              <p className="text-sm text-red-500 text-right">{error}</p>
            )}
            <div className="flex justify-end">
              <Button size="lg" disabled={!canSubmit} onClick={handleSubmit}>
                {submitting ? "Sending…" : "Send my check-in"}
                <ChevronRight className="ml-1 h-5 w-5" />
              </Button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default AIAgentVelocityCheckin;
